import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, Users, CheckSquare, Square, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const ro = language === 'ro';

  const [users, setUsers] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendResults, setSendResults] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/users`)
      .then(res => setUsers(res.data.filter(u => u.email && u.email !== 'admin@ecowash.ro' && u.email !== 'operator@ecowash.ro')))
      .catch(e => console.error(e));
  }, []);

  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase();
    return (
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.company_name?.toLowerCase().includes(q)
    );
  });

  const allSelected = filteredUsers.length > 0 && filteredUsers.every(u => selectedEmails.has(u.email));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedEmails(prev => {
        const next = new Set(prev);
        filteredUsers.forEach(u => next.delete(u.email));
        return next;
      });
    } else {
      setSelectedEmails(prev => {
        const next = new Set(prev);
        filteredUsers.forEach(u => { if (u.email) next.add(u.email); });
        return next;
      });
    }
  };

  const toggleUser = (email) => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim() || selectedEmails.size === 0) return;
    setSending(true);
    setSendResults(null);
    try {
      const res = await axios.post(`${API}/admin/email/send`, {
        subject: subject.trim(),
        body: body.trim(),
        emails: Array.from(selectedEmails),
      });
      setSendResults(res.data.results);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || (ro ? 'Eroare la trimiterea email-ului' : 'Error sending email'));
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    if (!subject.trim() || !body.trim() || !user?.email) return;
    setSendingTest(true);
    try {
      await axios.post(`${API}/admin/email/send`, {
        subject: `[TEST] ${subject.trim()}`,
        body: body.trim(),
        emails: [user.email],
      });
      toast.success(ro ? `Email de test trimis la ${user.email}` : `Test email sent to ${user.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || (ro ? 'Eroare la trimiterea testului' : 'Error sending test'));
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
          {ro ? 'Trimitere Email' : 'Send Email'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {ro ? 'Trimite email-uri utilizatorilor' : 'Send emails to users'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <Send className="w-4 h-4 text-primary" />
              {ro ? 'Mesaj' : 'Message'}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 flex-1">
            <Input
              placeholder={ro ? 'Subiect' : 'Subject'}
              value={subject}
              onChange={e => setSubject(e.target.value)}
            />
            <textarea
              className="w-full flex-1 min-h-[200px] rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={ro ? 'Scrie mesajul...' : 'Write your message...'}
              value={body}
              onChange={e => setBody(e.target.value)}
            />
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={handleSendTest}
                disabled={sendingTest || !subject.trim() || !body.trim() || !user?.email}
                className="gap-2"
                data-testid="send-test-email-btn"
              >
                <FlaskConical className="w-4 h-4" />
                {sendingTest
                  ? (ro ? 'Se trimite...' : 'Sending...')
                  : (ro ? 'Trimite test' : 'Send test')}
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || !subject.trim() || !body.trim() || selectedEmails.size === 0}
                className="gap-2"
              >
                <Send className="w-4 h-4" />
                {sending
                  ? (ro ? 'Se trimite...' : 'Sending...')
                  : `${ro ? 'Trimite' : 'Send'} (${selectedEmails.size})`}
              </Button>
            </div>

            {sendResults && (
              <div className="space-y-1 mt-2 max-h-40 overflow-y-auto">
                {sendResults.map((r, i) => (
                  <div key={i} className={`text-xs px-2 py-1 rounded flex justify-between ${r.ok ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}>
                    <span>{r.email}</span>
                    <span>{r.ok ? '✓' : `✗ ${r.error || ''}`}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <Users className="w-4 h-4 text-primary" />
              {ro ? 'Destinatari' : 'Recipients'}
              <span className="ml-auto text-xs font-normal text-muted-foreground">
                {selectedEmails.size} {ro ? 'selectați' : 'selected'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder={ro ? 'Caută utilizator...' : 'Search user...'}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div
              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer select-none border-b pb-2"
              onClick={toggleAll}
            >
              {allSelected
                ? <CheckSquare className="w-4 h-4 text-primary shrink-0" />
                : <Square className="w-4 h-4 text-muted-foreground shrink-0" />}
              <span className="text-sm font-medium">{ro ? 'Selectează toți' : 'Select all'}</span>
            </div>
            <div className="space-y-1 max-h-[280px] overflow-y-auto">
              {filteredUsers.map(u => (
                <div
                  key={u.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer select-none"
                  onClick={() => toggleUser(u.email)}
                >
                  {selectedEmails.has(u.email)
                    ? <CheckSquare className="w-4 h-4 text-primary shrink-0" />
                    : <Square className="w-4 h-4 text-muted-foreground shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{u.first_name} {u.last_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}{u.company_name ? ` · ${u.company_name}` : ''}</p>
                  </div>
                </div>
              ))}
              {filteredUsers.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {ro ? 'Niciun utilizator găsit' : 'No users found'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailPage;
